import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, User } from "lucide-react";

// 模擬今日工作項目資料
const mockTodayWorkItems = [
  {
    id: 1,
    name: "訂單 #2024001 - 組裝",
    department: "製造部",
    startTime: "08:00",
    endTime: "12:00",
    status: "進行中",
    priority: "高",
    assignedTo: "張三、李四"
  },
  {
    id: 2,
    name: "訂單 #2024002 - 品質檢驗",
    department: "品保部", 
    startTime: "09:00",
    endTime: "11:00",
    status: "待處理",
    priority: "中",
    assignedTo: "王五"
  },
  {
    id: 3,
    name: "客戶A需求確認",
    department: "業務部",
    startTime: "10:00", 
    endTime: "11:30",
    status: "已完成",
    priority: "高",
    assignedTo: "陳六"
  },
  {
    id: 4,
    name: "產品規格檢討",
    department: "技術部",
    startTime: "13:00",
    endTime: "16:00", 
    status: "進行中",
    priority: "中",
    assignedTo: "林七、趙八"
  },
  {
    id: 5,
    name: "訂單 #2024003 - 機械加工",
    department: "製造部",
    startTime: "13:30",
    endTime: "17:30",
    status: "待處理",
    priority: "高",
    assignedTo: "孫九"
  },
  {
    id: 6,
    name: "設備維護檢查",
    department: "技術部",
    startTime: "08:30",
    endTime: "10:00",
    status: "已完成",
    priority: "低",
    assignedTo: "周十"
  }
];

const departments = ["採購部", "包膠部", "倉庫部", "車床部", "品管部", "包裝部"];

// 生成時間軸 (8:00 到 18:00，每30分鐘一格)
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 8; hour <= 18; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      if (hour === 18 && minute > 0) break;
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push(timeString);
    }
  }
  return slots;
};

const timeSlots = generateTimeSlots();

// 將時間轉換為分鐘數 (從8:00開始計算)
const timeToMinutes = (timeString: string) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return (hours - 8) * 60 + minutes;
};

// 計算工作項目在時間軸上的位置和寬度
const calculatePosition = (startTime: string, endTime: string) => {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const totalMinutes = 10 * 60 + 30; // 8:00 到 18:30 總共630分鐘
  
  const left = (startMinutes / totalMinutes) * 100;
  const width = ((endMinutes - startMinutes) / totalMinutes) * 100;
  
  return { left: `${left}%`, width: `${width}%` };
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "已完成": return "bg-green-500";
    case "進行中": return "bg-primary"; 
    case "待處理": return "bg-yellow-500";
    default: return "bg-gray-500";
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "高": return "border-red-500";
    case "中": return "border-orange-500";
    case "低": return "border-gray-500";
    default: return "border-gray-300";
  }
};

// 獲取當前時間的位置
const getCurrentTimePosition = () => {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  
  // 如果當前時間在工作時間範圍內 (8:00-18:30)
  if (currentHour >= 8 && (currentHour < 18 || (currentHour === 18 && currentMinute <= 30))) {
    const currentTimeString = `${currentHour.toString().padStart(2, '0')}:${currentMinute.toString().padStart(2, '0')}`;
    const currentMinutes = timeToMinutes(currentTimeString);
    const totalMinutes = 10 * 60 + 30; // 8:00 到 18:30 總共630分鐘
    return (currentMinutes / totalMinutes) * 100;
  }
  return null; // 不在工作時間範圍內
};

export const TodayWorkSchedule: React.FC = () => {
  const [currentTimePosition, setCurrentTimePosition] = useState<number | null>(getCurrentTimePosition());

  // 每分鐘更新一次當前時間位置
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimePosition(getCurrentTimePosition());
    }, 60000); // 每分鐘更新

    return () => clearInterval(interval);
  }, []);
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          今日工作排程
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          {/* 單一共同時間軸標題 */}
          <div className="flex mb-4">
            <div className="w-24 flex-shrink-0"></div>
            <div className="flex-1 relative">
              <div className="flex text-xs text-gray-500 mb-2">
                {timeSlots.filter((_, index) => index % 2 === 0).map((time) => (
                  <div key={time} className="flex-1 text-center border-l border-gray-200 pl-2">
                    {time}
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-300"></div>
              
              {/* 當前時間標記 - 只顯示一次 */}
              {currentTimePosition !== null && (
                <div 
                  className="absolute -top-6 bg-primary text-white text-xs px-1.5 py-0.5 rounded whitespace-nowrap z-30"
                  style={{ left: `${currentTimePosition}%`, transform: 'translateX(-50%)' }}
                >
                  現在 {new Date().toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
          </div>

          {/* 部門行 - 共享時間軸 */}
          <div className="relative">
            {/* 時間分隔線 - 貫穿所有部門 */}
            <div className="absolute inset-0 pointer-events-none">
              {timeSlots.filter((_, index) => index % 2 === 0).map((time) => {
                const position = (timeToMinutes(time) / (10 * 60 + 30)) * 100;
                return (
                  <div 
                    key={time}
                    className="absolute top-0 bottom-0 border-l border-gray-200"
                    style={{ left: `calc(6rem + ${position}%)` }}
                  />
                );
              })}
              
              {/* 當前時間線 - 貫穿所有部門 */}
              {currentTimePosition !== null && (
                <div 
                  className="absolute top-0 bottom-0 w-0.5 bg-primary z-20"
                  style={{ left: `calc(6rem + ${currentTimePosition}%)` }}
                >
                  <div className="absolute -top-1 -left-1 w-2 h-2 bg-primary rounded-full"></div>
                </div>
              )}
            </div>

            {/* 部門內容 */}
            <div className="space-y-2">
              {departments.map((department) => (
                <div key={department} className="flex items-center">
                  {/* 部門名稱 */}
                  <div className="w-24 flex-shrink-0 pr-4">
                    <div className="text-sm font-medium text-right">{department}</div>
                  </div>
                  
                  {/* 工作項目區域 */}
                  <div className="flex-1 relative h-16 bg-gray-50 rounded border">
                    {/* 工作項目 */}
                    {mockTodayWorkItems
                      .filter(item => item.department === department)
                      .map((item, index) => {
                        const position = calculatePosition(item.startTime, item.endTime);
                        return (
                          <div
                            key={item.id}
                            className={`absolute h-12 rounded-md border-2 ${getPriorityColor(item.priority)} ${getStatusColor(item.status)} text-white text-xs p-1 cursor-pointer hover:shadow-lg transition-shadow`}
                            style={{ 
                              ...position,
                              top: `${index * 16 + 2}px`,
                              zIndex: 10
                            }}
                            title={`${item.name}\n時間: ${item.startTime} - ${item.endTime}\n負責人: ${item.assignedTo}\n狀態: ${item.status}`}
                          >
                            <div className="truncate font-medium text-xs leading-tight">
                              {item.name}
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              <Clock className="h-2 w-2" />
                              <span className="text-xs">{item.startTime}-{item.endTime}</span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 圖例 */}
          <div className="mt-6 flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>已完成</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-primary rounded"></div>
              <span>進行中</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-500 rounded"></div>
              <span>待處理</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 border-2 border-red-500 rounded"></div>
              <span>高優先級</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 border-2 border-orange-500 rounded"></div>
              <span>中優先級</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 border-2 border-gray-500 rounded"></div>
              <span>低優先級</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};